/**
 * ВИДИМАЯ ПОЛОСА ХОЛСТА — ТА ЧАСТЬ КАДРА, КОТОРУЮ НЕ ЗАКРЫВАЕТ ХРОМ.
 *
 * ЗАЧЕМ ЭТОТ СЛОЙ ВООБЩЕ ПОЯВИЛСЯ.
 *
 * Вписывание (`fitCameraToBox`, `fitOrthographicCameraToBox`) решает честную,
 * но НЕ ТУ задачу: «уместить все восемь углов коробки во фрустум». Фрустум —
 * это весь холст. А холст у Luminous закрыт сверху островом навигации и снизу
 * лентой этапов: они лежат НАД сценой (`position:absolute`), и модель уезжает
 * под них. Владелец увидел это на шкафу и сказал прямо: «нижнее меню
 * перекрывает шкаф». Замер на живом стенде 1024×744: лента 820×150, её верх на
 * y=536 — снизу закрыто 208 px, то есть 28 % высоты холста; сверху ещё 72 px.
 * Модель центрировалась в 744 px, из которых ~280 не видно.
 *
 * ПОЧЕМУ ЗАМЕР, А НЕ КОНСТАНТА.
 *
 * Высоты хрома не фиксированы. Лента объявлена как `min-height:136px` и растёт
 * от содержимого (в замере — 150). На узких экранах у неё другие правила
 * (`@media` 1439/767 в `luminous-shell.css`), а панели и орбиты появляются и
 * исчезают по состоянию. Вписанное в код число 208 или 150 сделало бы пин
 * ложно-зелёным на первой же правке вёрстки: код продолжал бы «знать» высоту,
 * которой на экране уже нет. Поэтому здесь читаются РЕАЛЬНЫЕ прямоугольники —
 * `getBoundingClientRect()` тех самых элементов, что рисуются.
 *
 * ОТКУДА БЕРЁТСЯ СПИСОК ХРОМА.
 *
 * `data-layout-zone` — уже существующий реестр зон оболочки: им помечены остров
 * навигации, лента этапов, орбиты, панели, статус-строка, и по нему же
 * `LuminousShell.layout.test.tsx` проверяет, что зоны не налезают друг на
 * друга. Второго списка заводить нельзя: разойдясь, «что нарисовано» и «что
 * считается закрытым» начали бы спорить, и модель снова уехала бы под ленту —
 * теперь уже молча, потому что код был бы уверен, что всё учтено.
 *
 * ПОСТОЯННАЯ РАМА ПРОТИВ ВРЕМЕННОЙ ПОВЕРХНОСТИ — ЗАМЕРЕНО, НЕ УГАДАНО.
 *
 * Первая версия считала хромом ВСЁ, что лежит над холстом. Это оказалось хуже
 * исходного дефекта, и вот почему — замер на стенде. Переключатель проекции
 * живёт ВНУТРИ панели «Проекция и виды» (996×256 сверху). Смена проекции
 * вписывает сцену, пока панель открыта: модель честно уходит вниз, под панель
 * её не видно. Человек закрывает панель — и модель оказывается ровно на ленте
 * этапов: 62.3 % площади под лентой против 29.05 % до всей этой работы. Кадр не
 * пересчитывается на закрытие панели, и «правильный» кадр протухает мгновенно.
 *
 * Поэтому вписывание считает только ПОСТОЯННУЮ РАМУ — то, что остаётся на
 * экране и после того, как человек закрыл всё закрываемое. Оболочка объявляет
 * это сама, рядом с зоной: `data-layout-surface="frame"` против
 * `"transient"`. Это не второй список: признак стоит там же, где и зона, и
 * пин `LuminousShell.layout.test.tsx` краснеет на зоне без объявления —
 * добавить поверхность и промолчать нельзя.
 *
 * Третье заявление — `"dock"`: панель, которая встаёт в поток (`.luminous-left-dock`,
 * `position:static`, VIS-15), сжимает САМ элемент холста, а не ложится поверх
 * него, поэтому в раме она не числится намеренно: её ширина уже внутри
 * `getBoundingClientRect()` канваса, и посчитать её вторично значило бы увести
 * камеру слишком далеко.
 *
 * Незаявленная зона в раму НЕ входит: молчание значит «обычная поверхность», и
 * ошибка объявления даёт прежнее поведение, а не новое молчаливое.
 *
 * РАМА, КОТОРАЯ БУДЕТ, А НЕ ТА, ЧТО СМОНТИРОВАНА, — ЗАМЕР ВТОРОГО КРУГА.
 *
 * Одного признака `frame` оказалось мало, и цена названа числом: путь первого
 * входа стал ХУЖЕ исходного дефекта — 14,45 % под лентой до всей работы против
 * 18,20 % после. Причина замерена: открытая панель «Проекция и виды» снимает с
 * экрана обе орбиты И ленту — все три зоны рамы разом, — и вписывание в этот
 * миг видит раму из одного `top-nav` в 72 px. Кадр опускает цель, модель едет
 * вниз навстречу ленте, которой сейчас нет и которая вернётся через клик.
 *
 * Поэтому зона рамы, единожды увиденная, ПОМНИТСЯ (см. `frameZoneRects`): её
 * прямоугольник продолжает сужать кадр, пока она снята. Это и есть «обещание
 * постоянства», выраженное так, что его можно проверить, — и заодно это
 * успокаивает пересчёт: открытие и закрытие панели отпечаток рамы
 * (`frameGeometrySignature`) не меняют, значит камеру не дёргают.
 *
 * ЧЕГО ЭТОТ СЛОЙ НЕ ДЕЛАЕТ.
 *
 * Он не двигает хром и не делает его прозрачным — это решение о ВИДЕ, и оно
 * принимается не здесь. Он только называет вписыванию, какая часть холста
 * действительно видна.
 */

/**
 * Насколько хром вгрызается в холст с каждой стороны, в пикселях CSS.
 * Ноль со всех сторон — «холст открыт целиком», и тогда вписывание обязано
 * посчитать РОВНО то же, что считало до появления этого слоя.
 */
export type FrameInsets = Readonly<{
  top: number;
  bottom: number;
  left: number;
  right: number;
}>;

export const NO_FRAME_INSETS: FrameInsets = Object.freeze({ top: 0, bottom: 0, left: 0, right: 0 });

/**
 * Видимая полоса в координатах кадра (NDC: −1 — низ/лево, +1 — верх/право).
 *
 * Именно в этих числах вписыванию удобно решать задачу: полуразмер говорит,
 * НАСКОЛЬКО уменьшилась доступная часть кадра (и значит, насколько дальше надо
 * отойти), а центр — КУДА эта часть сместилась (и значит, куда надо увести
 * цель камеры). Одним отдалением не отделаться: закрытый снизу кадр не просто
 * меньше — он ещё и смещён вверх.
 */
export type VisibleBand = Readonly<{
  /** NDC-центр полосы по горизонтали; 0 — середина кадра. */
  centerX: number;
  /** NDC-центр полосы по вертикали; 0 — середина кадра, >0 — полоса выше середины. */
  centerY: number;
  /** Полуширина полосы в долях полукадра; 1 — весь кадр. */
  halfX: number;
  /** Полувысота полосы в долях полукадра; 1 — весь кадр. */
  halfY: number;
}>;

/** Открытый холст: полоса совпадает с кадром. Значение по умолчанию везде. */
export const FULL_FRAME_BAND: VisibleBand = Object.freeze({ centerX: 0, centerY: 0, halfX: 1, halfY: 1 });

/**
 * Минимальная доля холста, которая ОБЯЗАНА остаться видимой по каждой оси.
 *
 * Предохранитель, а не настройка вида. Хром бывает сколь угодно большим:
 * на телефоне панели разворачиваются почти во весь экран (`@media
 * max-width:767px` делает их `position:fixed` от края до края). Полоса в
 * несколько пикселей заставила бы вписывание отодвинуть камеру на порядки —
 * изделие стало бы точкой, и человек списал бы это на «сломался зум».
 * Ограничение снизу превращает недостижимое требование в честную деградацию:
 * часть модели останется под хромом, но модель останется видимой.
 */
export const MIN_VISIBLE_FRACTION = 0.4;

/** Прямоугольник в координатах окна — ровно то, что отдаёт `getBoundingClientRect()`. */
export type ReadRect = { left: number; top: number; right: number; bottom: number; width: number; height: number };

/** Прямоугольник рамы с именем своей зоны: по имени он и запоминается. */
export type FrameZoneRect = ReadRect & { zone: string; mounted: boolean };

/**
 * Селектор реестра зон оболочки. Вынесен константой, чтобы в дереве нельзя
 * было завести второй список хрома, не тронув это имя.
 */
export const LAYOUT_ZONE_SELECTOR = '[data-layout-zone]';

/**
 * Селектор ПОСТОЯННОЙ РАМЫ: зоны, объявившие себя частью неубираемой оболочки.
 * Ровно они сужают кадр вписывания — см. врезку «постоянная рама против
 * временной поверхности» в шапке файла.
 */
export const LAYOUT_FRAME_SELECTOR = '[data-layout-zone][data-layout-surface="frame"]';

function rectOf(element: Element): ReadRect {
  const r = element.getBoundingClientRect();
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width, height: r.height };
}

function finite(value: number): boolean {
  return Number.isFinite(value);
}

/**
 * СВЕСТИ ПРЯМОУГОЛЬНИК ХРОМА К ВСТАВКЕ ОТ ОДНОГО КРАЯ.
 *
 * Хром — это не рамка, а набор отдельных плиток, и из произвольного
 * прямоугольника поверх холста в общем случае прямоугольной вставки не
 * получить. Правило разбора — «с какого края дешевле отступить»: для каждой из
 * четырёх сторон считается, на сколько пикселей пришлось бы отойти, чтобы
 * плитка оказалась снаружи, и выбирается НАИМЕНЬШЕЕ из четырёх чисел.
 *
 * Это и есть «к какому краю притянут элемент», выраженное замером, а не
 * догадкой по классу. Лента 820×150 на холсте 1024×744 с верхом на y=536:
 * сверху пришлось бы отступить 686, слева и справа по 922, снизу — 208.
 * Выигрывает 208, и это ровно то число, которое назвал владелец.
 */
function insetFromRect(rect: ReadRect, canvas: ReadRect): FrameInsets {
  const fromTop = Math.min(canvas.height, Math.max(0, rect.bottom - canvas.top));
  const fromBottom = Math.min(canvas.height, Math.max(0, canvas.bottom - rect.top));
  const fromLeft = Math.min(canvas.width, Math.max(0, rect.right - canvas.left));
  const fromRight = Math.min(canvas.width, Math.max(0, canvas.right - rect.left));
  const smallest = Math.min(fromTop, fromBottom, fromLeft, fromRight);
  if (smallest === fromTop) return { top: fromTop, bottom: 0, left: 0, right: 0 };
  if (smallest === fromBottom) return { top: 0, bottom: fromBottom, left: 0, right: 0 };
  if (smallest === fromLeft) return { top: 0, bottom: 0, left: fromLeft, right: 0 };
  return { top: 0, bottom: 0, left: 0, right: fromRight };
}

/**
 * ПАМЯТЬ О РАМЕ: ЗОНА, КОТОРАЯ БУДЕТ, ПРОТИВ ЗОНЫ, КОТОРАЯ СМОНТИРОВАНА.
 *
 * Здесь закрывается упущение, стоившее пути первого входа лишних 3,75 п.п.
 * Замер на живом стенде: пока открыта панель «Проекция и виды»,
 * `surface === 'viewport-controls'`, и оболочка снимает С ЭКРАНА обе орбиты И
 * ленту этапов — все три зоны `frame`. Вписывание, случившееся в этот миг,
 * читало раму из ОДНОЙ зоны (`top-nav`, 72 px), опускало цель и уводило модель
 * вниз — ровно туда, где через один клик снова появится лента.
 *
 * Объявление `data-layout-surface="frame"` — это обещание ПОСТОЯНСТВА: «я
 * часть неубираемой оболочки». Временное размонтирование такой зоны — событие
 * отрисовки, а не решение о виде, и кадр от него меняться не должен. Поэтому
 * прямоугольник каждой зоны рамы запоминается по её имени и продолжает сужать
 * кадр, пока зона снята.
 *
 * Память принадлежит ДОКУМЕНТУ, а не модулю: два стенда в одном процессе (а
 * это ровно случай пинов) не должны видеть чужую раму.
 *
 * ПАМЯТЬ ПРИВЯЗАНА К РАЗМЕРУ ХОЛСТА, И ЭТО ГРАНИЦА, А НЕ ЭКОНОМИЯ.
 *
 * Раскладка у оболочки зависит от ширины: орбит нет ниже 768 px
 * (`showDesktopLaunchers`), лента исчезает при открытой рейке ниже 1440, у
 * ленты свои `@media` в `luminous-shell.css`. Прямоугольник, замеренный на
 * другом размере, — это ДОГАДКА о раскладке, которой не видели: планшет,
 * повёрнутый в портрет, получил бы вечный резерв под орбиты, которых там нет
 * вовсе, и кадр молча ужимался бы неизвестно ради чего.
 *
 * Поэтому зона помнится ровно для того холста, на котором её видели. После
 * ресайза память по ней снимается, и работает второй механизм — пересчёт кадра
 * на изменение рамы (`useCameraFrameRefit`): зона вернулась — кадр пересчитан.
 */
type RememberedZone = Readonly<{
  /** Прямоугольник в координатах ХОЛСТА, а не окна: холст мог переехать. */
  left: number;
  top: number;
  width: number;
  height: number;
  /** Размер холста в момент замера — на другом размере эта память недействительна. */
  canvasWidth: number;
  canvasHeight: number;
}>;

const FRAME_MEMORY = new WeakMap<object, Map<string, RememberedZone>>();
/** Допуск на дробные пиксели при сравнении размеров холста. */
const CANVAS_SIZE_EPSILON = 0.5;

function memoryFor(root: ParentNode): Map<string, RememberedZone> {
  const existing = FRAME_MEMORY.get(root);
  if (existing) return existing;
  const created = new Map<string, RememberedZone>();
  FRAME_MEMORY.set(root, created);
  return created;
}

/** Забыть раму — только для пинов: живому продукту забывать её незачем. */
export function forgetFrameZones(scope: ParentNode): void {
  FRAME_MEMORY.delete(scope);
}

function remember(root: ParentNode, zone: string, rect: ReadRect, canvas: ReadRect): void {
  memoryFor(root).set(zone, Object.freeze({
    left: rect.left - canvas.left,
    top: rect.top - canvas.top,
    width: rect.width,
    height: rect.height,
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
  }));
}

/**
 * Разложить запомненную зону обратно на холст.
 *
 * Отказ (null), если холст с тех пор изменил размер: раскладка оболочки зависит
 * от ширины, и переносить прямоугольник на размер, которого не видели, значит
 * выдумывать раму.
 */
function replay(zone: string, remembered: RememberedZone, canvas: ReadRect): FrameZoneRect | null {
  if (Math.abs(remembered.canvasWidth - canvas.width) > CANVAS_SIZE_EPSILON) return null;
  if (Math.abs(remembered.canvasHeight - canvas.height) > CANVAS_SIZE_EPSILON) return null;
  const left = canvas.left + remembered.left;
  const top = canvas.top + remembered.top;
  return {
    zone,
    mounted: false,
    left, top, right: left + remembered.width, bottom: top + remembered.height,
    width: remembered.width, height: remembered.height,
  };
}

/**
 * Прямоугольники ПОСТОЯННОЙ РАМЫ для этого холста.
 *
 * Возвращаются и нарисованные сейчас зоны, и запомненные — те, что объявили
 * себя рамой и временно сняты с экрана. Статус-строка живёт ПОД холстом и
 * потому в раму не входит: пересечения нет — не потому, что её вычеркнули из
 * списка, а потому, что она честно мимо.
 *
 * @param canvas холст сцены; его собственный прямоугольник задаёт систему отсчёта.
 * @param scope  где искать зоны; по умолчанию — документ холста.
 */
export function frameZoneRects(canvas: Element, scope?: ParentNode): FrameZoneRect[] {
  const canvasRect = rectOf(canvas);
  if (!(canvasRect.width > 0) || !(canvasRect.height > 0)) return [];
  const root = scope ?? canvas.ownerDocument ?? null;
  if (!root || typeof root.querySelectorAll !== 'function') return [];

  const overlapsCanvas = (rect: ReadRect) => rect.left < canvasRect.right && rect.right > canvasRect.left
    && rect.top < canvasRect.bottom && rect.bottom > canvasRect.top;

  const live: FrameZoneRect[] = [];
  const seen = new Set<string>();
  for (const element of root.querySelectorAll(LAYOUT_FRAME_SELECTOR)) {
    if (element === canvas || element.contains(canvas)) continue;
    const zone = (element as HTMLElement).dataset?.layoutZone;
    if (!zone) continue;
    const rect = rectOf(element);
    if (!(rect.width > 0) || !(rect.height > 0)) continue;
    seen.add(zone);
    remember(root, zone, rect, canvasRect);
    if (!overlapsCanvas(rect)) continue;
    live.push({ ...rect, zone, mounted: true });
  }

  const recalled: FrameZoneRect[] = [];
  const memory = memoryFor(root);
  for (const [zone, remembered] of [...memory]) {
    if (seen.has(zone)) continue;
    const rect = replay(zone, remembered, canvasRect);
    // Память, снятая на другом размере холста, не подновляется — она снимается:
    // держать её значило бы вечно платить за раму, которой на этом экране нет.
    if (rect === null) { memory.delete(zone); continue; }
    if (!(rect.width > 0) || !(rect.height > 0)) continue;
    if (!overlapsCanvas(rect)) continue;
    recalled.push(rect);
  }
  return [...live, ...recalled];
}

/** Свести набор прямоугольников рамы к вставкам от краёв холста. */
export function insetsFromRects(rects: readonly ReadRect[], canvasRect: ReadRect): FrameInsets {
  let top = 0;
  let bottom = 0;
  let left = 0;
  let right = 0;
  for (const rect of rects) {
    const inset = insetFromRect(rect, canvasRect);
    top = Math.max(top, inset.top);
    bottom = Math.max(bottom, inset.bottom);
    left = Math.max(left, inset.left);
    right = Math.max(right, inset.right);
  }
  return Object.freeze({ top, bottom, left, right });
}

/**
 * Замерить, сколько холста закрыто ПОСТОЯННОЙ РАМОЙ — нарисованной и обещанной.
 *
 * @param canvas холст сцены; его собственный прямоугольник задаёт систему отсчёта.
 * @param scope  где искать зоны; по умолчанию — документ холста.
 */
export function measureChromeInsets(canvas: Element, scope?: ParentNode): FrameInsets {
  const canvasRect = rectOf(canvas);
  if (!(canvasRect.width > 0) || !(canvasRect.height > 0)) return NO_FRAME_INSETS;
  return insetsFromRects(frameZoneRects(canvas, scope), canvasRect);
}

/**
 * Отпечаток рамы: меняется РОВНО тогда, когда меняется геометрия рамы.
 *
 * По нему решается, надо ли пересчитывать кадр. Временное снятие зоны отпечаток
 * НЕ меняет — её прямоугольник помнится, — и потому открытие панели не дёргает
 * камеру. Ресайз окна и первое появление зоны, которой ещё не видели, меняют.
 */
export function frameGeometrySignature(canvas: Element, scope?: ParentNode): string {
  const canvasRect = rectOf(canvas);
  if (!(canvasRect.width > 0) || !(canvasRect.height > 0)) return 'no-canvas';
  const round = (value: number) => Math.round(value * 100) / 100;
  const parts = frameZoneRects(canvas, scope)
    .map((rect) => `${rect.zone}:${round(rect.left - canvasRect.left)},${round(rect.top - canvasRect.top)},${round(rect.width)},${round(rect.height)}`)
    .sort();
  return `${round(canvasRect.width)}x${round(canvasRect.height)}|${parts.join('|')}`;
}

/** Ужать пару противоположных вставок так, чтобы полоса сохранила долю `minFraction`. */
function clampPair(near: number, far: number, size: number): { near: number; far: number } {
  if (!(size > 0)) return { near: 0, far: 0 };
  const a = finite(near) && near > 0 ? Math.min(near, size) : 0;
  const b = finite(far) && far > 0 ? Math.min(far, size) : 0;
  const allowed = size * (1 - MIN_VISIBLE_FRACTION);
  const demanded = a + b;
  if (demanded <= allowed) return { near: a, far: b };
  if (!(demanded > 0)) return { near: 0, far: 0 };
  // Ужимаем ОБЕ вставки одним множителем: их отношение — это смещение центра
  // полосы, и ужать только одну значило бы сдвинуть модель не туда, куда
  // показывает экран.
  const scale = allowed / demanded;
  return { near: a * scale, far: b * scale };
}

/**
 * Перевести вставки в видимую полосу кадра.
 *
 * Вывод. Верхний край полосы в NDC равен `1 − 2·top/H`, нижний `−1 + 2·bottom/H`.
 * Отсюда полувысота `1 − (top+bottom)/H`, а центр `(bottom − top)/H` — он
 * ПОЛОЖИТЕЛЕН, когда снизу закрыто больше: видимая полоса лежит выше середины
 * кадра, и модель обязана подняться вместе с ней.
 */
export function visibleBandFromInsets(insets: FrameInsets, widthPx: number, heightPx: number): VisibleBand {
  if (!finite(widthPx) || !finite(heightPx) || widthPx <= 0 || heightPx <= 0) return FULL_FRAME_BAND;
  const vertical = clampPair(insets.top, insets.bottom, heightPx);
  const horizontal = clampPair(insets.left, insets.right, widthPx);
  const halfY = 1 - (vertical.near + vertical.far) / heightPx;
  const halfX = 1 - (horizontal.near + horizontal.far) / widthPx;
  const centerY = (vertical.far - vertical.near) / heightPx;
  const centerX = (horizontal.near - horizontal.far) / widthPx;
  return Object.freeze({ centerX, centerY, halfX, halfY });
}

/** Замерить полосу для холста одной рукой: прямоугольники хрома → полоса кадра. */
export function measureVisibleBand(canvas: Element, scope?: ParentNode): VisibleBand {
  const rect = rectOf(canvas);
  return visibleBandFromInsets(measureChromeInsets(canvas, scope), rect.width, rect.height);
}

/** Полоса по ЗАДАННОМУ набору прямоугольников — так вписывание пробует сузить резерв. */
export function visibleBandFromRects(rects: readonly ReadRect[], canvasRect: ReadRect): VisibleBand {
  return visibleBandFromInsets(insetsFromRects(rects, canvasRect), canvasRect.width, canvasRect.height);
}

/** Прямоугольник холста как система отсчёта — одно имя на весь слой. */
export function canvasReferenceRect(canvas: Element): ReadRect {
  return rectOf(canvas);
}

/** Пересекаются ли два прямоугольника (касание краями пересечением не считается). */
export function rectsOverlap(a: ReadRect, b: ReadRect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}
